import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { FormViewer, createView, define, event, number, string } from "@react-form-builder/core";
import { signatureComponent } from "@react-form-builder/components-signature";
import { models, MuiViewerWrapper, MuiLocalizationProvider } from "@react-form-builder/components-material-ui";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import { toast } from "react-toastify";
import { getImageUrl } from "src/Common/common";
import ConfirmDialog from "src/Common/confirmdialog";
import { handleError, isSuperAdmin } from "src/Common/function";
import WOFormService from "src/Services/API/woform.service";
import { drawStoredSignatures, generateFormPdf } from "./formPdf";
import RSCSLogo from "./customComponents/RSCSLogo";
import TechnicianDropdown from "./customComponents/TechnicianDropdown";

const signatureModel = signatureComponent.build().model;
// Type id "RSCSLogo" kept for saved-form compatibility; renders the current
// company's logo (see RSCSLogo.jsx).
const rscsLogoModel = define(RSCSLogo, "RSCSLogo")
  .name("Company Logo")
  .category("Custom")
  .props({
    width: number.default(140),
    height: string.default("auto"),
    alt: string.default("Company Logo"),
  })
  .build().model;

const technicianDropdownModel = define(TechnicianDropdown, "TechnicianDropdown")
  .name("Technician Dropdown")
  .category("Custom")
  .props({
    value: string.valued,
    woId: number.default(0),
    label: string.default("Technician"),
    placeholder: string.default("Select technician"),
    onChange: event,
  })
  .build().model;

const customView = createView([...models, signatureModel, rscsLogoModel, technicianDropdownModel])
  .withViewerWrapper(MuiViewerWrapper)
  .withViewerWrapper(MuiLocalizationProvider);

const FormViewerModal = ({ shown, title = "Form Viewer", forms = [], onClose, onUnassign }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  const [showUnassignConfirm, setShowUnassignConfirm] = useState(false);
  const formContentRef = useRef(null);

  useEffect(() => {
    if (shown) setCurrentIndex(0);
  }, [shown]);

  useEffect(() => {
    if (!shown) {
      setShowUnassignConfirm(false);
    }
  }, [shown]);

  useEffect(() => {
    if (forms.length > 0 && currentIndex > forms.length - 1) {
      setCurrentIndex(forms.length - 1);
    }
  }, [forms, currentIndex]);

  useEffect(() => {
    const currentWoId = forms?.[currentIndex]?.wo?.id;
    window.__makaniFormWoId = currentWoId ? Number(currentWoId) : null;
    return () => {
      delete window.__makaniFormWoId;
    };
  }, [forms, currentIndex]);

  const currentForm = forms[currentIndex];

  const getForm = useCallback(() => {
    if (!currentForm) return "{}";
    const schema = currentForm.schema || currentForm;
    return typeof schema === "string" ? schema : JSON.stringify(schema);
  }, [currentForm]);

  const initialData = useMemo(() => {
    if (!currentForm?.data) return undefined;
    return typeof currentForm.data === "string" ? JSON.parse(currentForm.data) : currentForm.data;
  }, [currentForm]);

  const setRef = useCallback((viewer) => {
    if (viewer) {
      window.__formViewer = viewer;
    }
  }, []);

  // The form viewer applies stored data after mount, but stored signatures are
  // only drawn into their canvases on mount — retry on timers until the data
  // has landed (drawStoredSignatures is a no-op on non-blank canvases).
  useEffect(() => {
    if (!shown || !initialData) return undefined;
    const timers = [300, 800, 1500].map((delay) =>
      setTimeout(() => {
        drawStoredSignatures(formContentRef.current, initialData, getForm());
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [shown, currentIndex, initialData, getForm]);

  const handleDownloadPdf = useCallback(async () => {
    if (!formContentRef.current || downloading) return;
    setDownloading(true);
    try {
      const element = formContentRef.current;
      const scrollContainer = element.parentElement;

      const prevOverflow = scrollContainer.style.overflow;
      const prevMaxHeight = scrollContainer.style.maxHeight;
      scrollContainer.style.overflow = "visible";
      scrollContainer.style.maxHeight = "none";

      try {
        await drawStoredSignatures(element, initialData, getForm());
        const pdf = await generateFormPdf(element);
        const fileName = currentForm?.name || title || "form";
        pdf.save(`${fileName}.pdf`);
      } finally {
        scrollContainer.style.overflow = prevOverflow;
        scrollContainer.style.maxHeight = prevMaxHeight;
      }
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [downloading, currentForm, title, initialData, getForm]);

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < forms.length - 1;
  const canUnassign =
    currentForm?.id &&
    (currentForm?.status === 1 || ((currentForm?.status === 2 || currentForm?.status === 3) && isSuperAdmin())) &&
    !unassigning;

  const handleUnassign = useCallback(async () => {
    if (!canUnassign) return;

    setUnassigning(true);
    try {
      await WOFormService.update(currentForm.id, {
        status: 0,
        wo: null,
      });
      toast("Form unassigned successfully");
      if (onUnassign) {
        await onUnassign(currentForm);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setUnassigning(false);
    }
  }, [currentForm, onUnassign, unassigning]);

  if (!shown) return null;

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
      <OutsideClickHandler
        onOutsideClick={() => {
          if (!showUnassignConfirm) onClose();
        }}
      >
        <div className="bg-white rounded-md w-[700px] h-[90vh] px-3 py-3 relative flex flex-col overflow-hidden">
          <div className="flex items-center justify-between mb-3 mt-2 px-1 border-b border-borderColor pb-3">
            {/* <div className="w-[24px]" /> */}
            <p className="font-OpenSansRegular text-[16px] font-[700] text-titleColor">{title}</p>
            <div className="flex items-center gap-2">
              {currentForm?.isSubmitted && (
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloading}
                  className="flex items-center gap-1.5 bg-blueColor hover:bg-blue-700 text-white px-3 py-1.5 rounded-[6px] font-OpenSansRegular font-[600] text-[11px] transition-colors disabled:opacity-60"
                >
                  <img src={getImageUrl("/images/common/download.png")} alt="download" className="w-[14px] h-[14px]" />
                  {downloading ? "DOWNLOADING..." : "DOWNLOAD"}
                </button>
              )}
              <button
                onClick={() => setShowUnassignConfirm(true)}
                disabled={!canUnassign}
                className="flex items-center gap-1.5 bg-[#D9534F] hover:bg-[#C9302C] text-white px-3 py-1.5 rounded-[6px] font-OpenSansRegular font-[600] text-[11px] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {unassigning ? "UNASSIGNING..." : "UNASSIGN"}
              </button>
              <img
                src={getImageUrl("/images/common/close.png")}
                className="w-[24px] cursor-pointer"
                onClick={onClose}
              />
            </div>
          </div>
          <div className="p-2 flex-1 min-h-0">
            <div
              className="h-full border border-gray-300 rounded-md relative overflow-hidden"
              style={{ margin: "0 auto", width: "100%" }}
            >
              <div className="h-full overflow-y-auto p-[10px]">
                <div ref={formContentRef} style={{ pointerEvents: "none" }}>
                  <FormViewer
                    key={currentIndex}
                    view={customView}
                    getForm={getForm}
                    initialData={initialData}
                    viewerRef={setRef}
                  />
                </div>
              </div>
              {currentForm?.isSubmitted && (
                <div
                  className="absolute inset-0 bg-[#8BD38E] bg-opacity-20 rounded-md pointer-events-none"
                  style={{ zIndex: 10 }}
                />
              )}
            </div>
          </div>

          {forms.length > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 mt-2">
              <button
                onClick={() => setCurrentIndex((i) => i - 1)}
                disabled={!hasPrev}
                className={`px-4 py-1.5 rounded-[6px] font-OpenSansRegular font-[600] text-[11px] ${
                  hasPrev ? "bg-blueColor text-white cursor-pointer" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                Previous
              </button>

              <span className="font-OpenSansMedium text-[14px] text-titleColor">
                {forms[currentIndex].name}{" "}
                <span className="font-OpenSansRegular text-[12px] text-gray-500">
                  {currentIndex + 1} of {forms.length}
                </span>
              </span>

              <button
                onClick={() => setCurrentIndex((i) => i + 1)}
                disabled={!hasNext}
                className={`px-4 py-1.5 rounded-[6px] font-OpenSansRegular font-[600] text-[11px] ${
                  hasNext ? "bg-blueColor text-white cursor-pointer" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </OutsideClickHandler>
      <ConfirmDialog
        title="UNASSIGN FORM"
        description="Are you sure you want to unassign this form from the Work Order?"
        shown={showUnassignConfirm}
        onCancelClicked={() => setShowUnassignConfirm(false)}
        onConfirmClicked={async () => {
          setShowUnassignConfirm(false);
          await handleUnassign();
        }}
      />
    </div>
  );
};

export default FormViewerModal;
